import { useEffect, useRef, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as authApi from '../api/auth.api';
import storage from '../utils/storage';
import Logger from '../utils/logger';

/**
 * Activation de compte
 * - Gère le cas "déjà activé" comme un succès métier
 * - Un seul appel à la fois
 * - Nettoyage propre
 */
export const useActivateAccount = () => {
  const abortControllerRef = useRef(null);

  const activateMutation = useMutation({
    mutationFn: async (token) => {
      if (!token || typeof token !== 'string' || !token.trim()) {
        throw new Error('Token invalide ou manquant');
      }

      abortControllerRef.current = new AbortController();

      try {
        Logger.log('Activation du compte...', { tokenLength: token.length });

        const response = await authApi.activateAccount(token, {
          signal: abortControllerRef.current.signal,
        });

        return {
          status: 'activated',
          data: response,
        };
      } catch (error) {
        const apiMessage =
          error.response?.data?.token?.[0] ||
          error.response?.data?.message;

        // ✅ CAS MÉTIER : compte déjà activé
        if (
          apiMessage?.toLowerCase().includes('déjà') &&
          apiMessage?.toLowerCase().includes('activ')
        ) {
          Logger.warn('Compte déjà activé');

          return {
            status: 'already_activated',
            message: apiMessage,
          };
        }

        Logger.error('Erreur activation compte', {
          status: error.response?.status,
          message: apiMessage || error.message,
        });

        throw error;
      }
    },

    onSuccess: (result) => {
      if (result.status === 'activated') {
        const user = result.data?.user;
        if (user) {
          storage.setUser(user);
        }
      }
    },

    retry: false,
  });

  const activate = useCallback(
    (token) => {
      if (activateMutation.isPending || activateMutation.isSuccess) return;
      activateMutation.mutate(token);
    },
    [activateMutation]
  );

  const reset = useCallback(() => {
    activateMutation.reset();
  }, [activateMutation]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return {
    // actions
    activate,
    reset,

    // états
    isPending: activateMutation.isPending,
    isSuccess: activateMutation.isSuccess,
    isError: activateMutation.isError,

    // données métier
    status: activateMutation.data?.status || null,
    message: activateMutation.data?.message || null,

    // erreur réelle
    error:
      activateMutation.error?.response?.data?.message ||
      activateMutation.error?.message ||
      null,
  };
};
